<template>
  <div class="contact-detail-page">
    <!-- Debug info -->
    <div v-if="debugMode" class="debug-info pa-2 mb-4 bg-grey-lighten-4">
      <div class="text-caption">Debug Info:</div>
      <div class="text-caption">Route ID: {{ route.params.id }}</div>
      <div class="text-caption">Contact ID from store: {{ contact?.id }}</div>
      <div class="text-caption">Loading: {{ isLoading }}</div>
      <div class="text-caption">Error: {{ error }}</div>
      <div class="text-caption">
        Store entities count: {{ contactsStore.entities?.length || 0 }}
      </div>
    </div>

    <!-- Loading state -->
    <div v-if="isLoading" class="text-center py-8">
      <v-progress-circular indeterminate color="primary" size="48" />
      <div class="text-body-2 text-medium-emphasis mt-2">Contact laden...</div>
    </div>

    <!-- Error state -->
    <div v-else-if="error" class="text-center py-8">
      <v-alert type="error" variant="tonal" class="mb-4" :text="error" />
      <v-btn
        color="primary"
        variant="elevated"
        to="/contacts"
        prepend-icon="mdi-arrow-left"
      >
        Terug naar contacten
      </v-btn>
    </div>

    <!-- Main content -->
    <div v-else-if="contact" class="contact-content">
      <!-- Breadcrumb Navigation -->
      <nav v-if="breadcrumbItems.length > 1" class="breadcrumb-nav mb-4">
        <v-breadcrumbs :items="breadcrumbItems" class="pa-0" density="compact">
          <template #prepend>
            <v-icon icon="mdi-home" size="small" />
          </template>

          <template #item="{ item }">
            <v-breadcrumbs-item
              :to="item.to"
              :disabled="item.disabled"
              class="breadcrumb-item"
            >
              <div class="d-flex align-center">
                <v-avatar
                  v-if="item.contact"
                  :color="getContactColor(item.contact.type)"
                  size="20"
                  class="mr-2"
                >
                  <v-icon
                    :icon="getContactIcon(item.contact.type)"
                    color="white"
                    size="12"
                  />
                </v-avatar>
                <span>{{ item.title }}</span>
              </div>
            </v-breadcrumbs-item>
          </template>

          <template #divider>
            <v-icon icon="mdi-chevron-right" size="small" class="mx-1" />
          </template>
        </v-breadcrumbs>
      </nav>

      <!-- Main contact info -->
      <div class="contact-header mb-6">
        <div class="d-flex align-center mb-4">
          <v-avatar
            :color="getContactColor(contact.type)"
            size="56"
            class="mr-4 elevation-2"
          >
            <v-icon
              :icon="getContactIcon(contact.type)"
              color="white"
              size="28"
            />
          </v-avatar>
          <div class="flex-grow-1">
            <h1 class="text-h4 mb-1">
              {{ contact.display_name || contact.name || "Naamloos contact" }}
              <span
                v-if="primaryContactInfo"
                class="text-body-1 text-medium-emphasis font-weight-normal ml-3"
              >
                {{ primaryContactInfo }}
              </span>
            </h1>
            <div class="d-flex align-center flex-wrap ga-2">
              <v-chip
                :color="getContactColor(contact.type)"
                size="default"
                variant="flat"
              >
                {{ getContactTypeLabel(contact.type) }}
              </v-chip>

              <!-- Contact categories -->
              <v-chip
                v-for="category in contactCategories"
                :key="category.id"
                :color="category.color || 'grey'"
                size="default"
                variant="tonal"
              >
                <template #prepend>
                  <v-icon
                    :icon="category.icon || 'mdi-folder'"
                    size="18"
                    class="mr-1"
                  />
                </template>
                {{ category.name }}
              </v-chip>
            </div>
          </div>
          <v-btn icon variant="text" size="large" @click="editContact">
            <v-icon>mdi-pencil</v-icon>
          </v-btn>
        </div>
      </div>

      <!-- Contact details -->
      <div class="contact-info-grid mb-6">
        <!-- Contact Information Card -->
        <div class="info-card">
          <div class="info-card-header">
            <v-icon class="mr-2" color="primary"
              >mdi-card-account-details</v-icon
            >
            <h3 class="text-h6">Contactinformatie</h3>
          </div>
          <div class="info-card-content">
            <!-- Email addresses -->
            <div v-if="contactEmails.length" class="contact-detail-group mb-4">
              <div class="detail-group-title">E-mailadressen</div>
              <div
                v-for="email in contactEmails"
                :key="email.id"
                class="contact-item"
              >
                <div class="contact-item-icon">
                  <v-icon size="20" color="primary">mdi-email</v-icon>
                </div>
                <div class="contact-item-content">
                  <div class="contact-item-value">
                    <a
                      :href="`mailto:${email.email}`"
                      class="text-decoration-none"
                    >
                      {{ email.email }}
                    </a>
                    <v-chip
                      v-if="email.primary"
                      size="x-small"
                      color="primary"
                      variant="tonal"
                      class="ml-2"
                    >
                      Primair
                    </v-chip>
                  </div>
                </div>
              </div>
            </div>

            <!-- Phone numbers -->
            <div v-if="contactPhones.length" class="contact-detail-group mb-4">
              <div class="detail-group-title">Telefoonnummers</div>
              <div
                v-for="phone in contactPhones"
                :key="phone.id"
                class="contact-item"
              >
                <div class="contact-item-icon">
                  <v-icon size="20" color="primary">mdi-phone</v-icon>
                </div>
                <div class="contact-item-content">
                  <div class="contact-item-label">
                    {{ phone.phone_type_label || phone.phone_type }}
                  </div>
                  <div class="contact-item-value">
                    <a
                      :href="`tel:${phone.phone_number}`"
                      class="text-decoration-none"
                    >
                      {{ phone.phone_number }}
                    </a>
                    <v-chip
                      v-if="phone.primary"
                      size="x-small"
                      color="primary"
                      variant="tonal"
                      class="ml-2"
                    >
                      Primair
                    </v-chip>
                    <div
                      v-if="phone.meta"
                      class="text-caption text-medium-emphasis"
                    >
                      {{ phone.meta }}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Locations (new approach) -->
            <div
              v-if="contactLocations.length"
              class="contact-detail-group mb-4"
            >
              <div class="detail-group-title">Locaties</div>
              <div
                v-for="linkedLocation in contactLocations"
                :key="linkedLocation.id"
                class="contact-item"
              >
                <div class="contact-item-icon">
                  <v-icon size="20" color="primary">mdi-map-marker</v-icon>
                </div>
                <div class="contact-item-content">
                  <div class="contact-item-value">
                    <div>
                      {{
                        linkedLocation.location.display_name ||
                        linkedLocation.location.formatted_address
                      }}
                    </div>
                    <v-chip
                      v-if="linkedLocation.primary"
                      size="x-small"
                      color="amber-darken-3"
                      variant="tonal"
                      class="mt-1"
                    >
                      Primair
                    </v-chip>
                  </div>
                </div>
              </div>
            </div>

            <!-- Addresses (legacy - shown if present) -->
            <div
              v-if="contactAddresses.length"
              class="contact-detail-group mb-4"
            >
              <div class="detail-group-title">
                Adressen (legacy)
                <v-chip
                  size="x-small"
                  color="warning"
                  variant="tonal"
                  class="ml-2"
                >
                  Verouderd
                </v-chip>
              </div>
              <div
                v-for="address in contactAddresses"
                :key="address.id"
                class="contact-item"
              >
                <div class="contact-item-icon">
                  <v-icon size="20" color="warning"
                    >mdi-map-marker-alert</v-icon
                  >
                </div>
                <div class="contact-item-content">
                  <div class="contact-item-value">
                    <div>{{ address.street }} {{ address.number }}</div>
                    <div>{{ address.postal_code }} {{ address.city }}</div>
                    <v-chip
                      v-if="address.primary"
                      size="x-small"
                      color="primary"
                      variant="tonal"
                      class="mt-1"
                    >
                      Primair
                    </v-chip>
                  </div>
                </div>
              </div>
            </div>

            <!-- Legacy single contact info (fallback) -->
            <div
              v-if="
                !hasAnyContactDetails &&
                (contact.primary_email_address ||
                  contact.primary_phone_formatted)
              "
              class="contact-detail-group"
            >
              <div class="detail-group-title">Contactgegevens (legacy)</div>

              <!-- Legacy Email -->
              <div v-if="contact.primary_email_address" class="contact-item">
                <div class="contact-item-icon">
                  <v-icon size="20" color="primary">mdi-email</v-icon>
                </div>
                <div class="contact-item-content">
                  <div class="contact-item-label">E-mail</div>
                  <div class="contact-item-value">
                    <a
                      :href="`mailto:${contact.primary_email_address}`"
                      class="text-decoration-none"
                    >
                      {{ contact.primary_email_address }}
                    </a>
                  </div>
                </div>
              </div>

              <!-- Legacy Phone -->
              <div v-if="contact.primary_phone_formatted" class="contact-item">
                <div class="contact-item-icon">
                  <v-icon size="20" color="primary">mdi-phone</v-icon>
                </div>
                <div class="contact-item-content">
                  <div class="contact-item-label">Telefoon</div>
                  <div class="contact-item-value">
                    <a
                      :href="`tel:${contact.primary_phone_formatted}`"
                      class="text-decoration-none"
                    >
                      {{ contact.primary_phone_formatted }}
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <!-- Placeholder if no details -->
            <div
              v-if="
                !hasAnyContactDetails &&
                !contact.primary_email_address &&
                !contact.primary_phone_formatted
              "
              class="empty-state"
            >
              <v-icon size="48" color="grey-lighten-1" class="mb-2"
                >mdi-information-outline</v-icon
              >
              <div class="text-body-2 text-medium-emphasis">
                Nog geen contactgegevens toegevoegd
              </div>
              <v-btn
                variant="text"
                size="small"
                class="mt-2"
                @click="editContact"
              >
                <v-icon start>mdi-plus</v-icon>
                Toevoegen
              </v-btn>
            </div>
          </div>
        </div>

        <!-- Notes Card -->
        <div class="info-card">
          <div class="info-card-header">
            <v-icon class="mr-2" color="primary">mdi-text-box-multiple</v-icon>
            <h3 class="text-h6">Notities</h3>
          </div>
          <div class="info-card-content">
            <!-- Notes list -->
            <div v-if="contactNotes.length" class="notes-list">
              <div
                v-for="note in contactNotes"
                :key="note.id"
                class="note-item"
              >
                <div class="note-header d-flex align-center mb-2">
                  <v-chip
                    size="x-small"
                    color="blue-grey"
                    variant="tonal"
                    class="mr-2"
                  >
                    {{ note.note_type_label || note.note_type }}
                  </v-chip>
                  <span
                    v-if="note.note_date"
                    class="text-caption text-medium-emphasis"
                  >
                    {{ formatDate(note.note_date) }}
                  </span>
                </div>
                <div class="note-content text-body-2">
                  {{ note.note }}
                </div>
              </div>
            </div>

            <!-- Empty state for notes -->
            <div v-else class="empty-state">
              <v-icon size="48" color="grey-lighten-1" class="mb-2"
                >mdi-text-box-outline</v-icon
              >
              <div class="text-body-2 text-medium-emphasis">
                Nog geen notities toegevoegd
              </div>
              <v-btn
                variant="text"
                size="small"
                class="mt-2"
                @click="editContact"
              >
                <v-icon start>mdi-plus</v-icon>
                Notitie toevoegen
              </v-btn>
            </div>
          </div>
        </div>
      </div>

      <!-- Children section for organizations and departments -->
      <div
        v-if="contact.type === 'organization' || contact.type === 'department'"
        class="children-section"
      >
        <!-- Departments (only for organizations) -->
        <div v-if="contact.type === 'organization'" class="children-group mb-6">
          <div class="children-header">
            <div class="d-flex align-center justify-space-between mb-3">
              <div class="d-flex align-center">
                <v-icon color="amber-darken-2" class="mr-2">mdi-domain</v-icon>
                <h3 class="text-h6">Afdelingen</h3>
                <v-chip
                  v-if="safeChildDepartments.length > 0"
                  size="small"
                  variant="tonal"
                  color="amber-darken-2"
                  class="ml-2"
                >
                  {{ safeChildDepartments.length }}
                </v-chip>
              </div>
              <v-btn
                color="amber-darken-2"
                variant="elevated"
                size="small"
                @click="addChildDepartment"
              >
                <v-icon start>mdi-plus</v-icon>
                Afdeling toevoegen
              </v-btn>
            </div>
          </div>

          <div v-if="safeChildDepartments.length > 0" class="children-list">
            <ContactListItem
              v-for="department in safeChildDepartments"
              :key="department.id"
              :contact="department"
              show-parent-info
              class="child-contact-item mb-3"
            />
          </div>

          <div v-else class="empty-children-inline">
            <div class="text-body-2 text-medium-emphasis text-center py-8">
              <v-icon size="48" color="grey-lighten-1" class="mb-2"
                >mdi-domain</v-icon
              >
              <div>Nog geen afdelingen toegevoegd</div>
            </div>
          </div>
        </div>

        <!-- People -->
        <div class="children-group">
          <div class="children-header">
            <div class="d-flex align-center justify-space-between mb-3">
              <div class="d-flex align-center">
                <v-icon color="green-darken-2" class="mr-2"
                  >mdi-account-multiple</v-icon
                >
                <h3 class="text-h6">Personen</h3>
                <v-chip
                  v-if="safeChildPeople.length > 0"
                  size="small"
                  variant="tonal"
                  color="green-darken-2"
                  class="ml-2"
                >
                  {{ safeChildPeople.length }}
                </v-chip>
              </div>
              <v-btn
                color="green-darken-2"
                variant="elevated"
                size="small"
                @click="addChildPerson"
              >
                <v-icon start>mdi-plus</v-icon>
                Persoon toevoegen
              </v-btn>
            </div>
          </div>

          <div v-if="safeChildPeople.length > 0" class="children-list">
            <ContactListItem
              v-for="person in safeChildPeople"
              :key="person.id"
              :contact="person"
              show-parent-info
              class="child-contact-item mb-3"
            />
          </div>

          <div v-else class="empty-children-inline">
            <div class="text-body-2 text-medium-emphasis text-center py-8">
              <v-icon size="48" color="grey-lighten-1" class="mb-2"
                >mdi-account-multiple</v-icon
              >
              <div>Nog geen personen toegevoegd</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- No contact found -->
    <div v-else class="text-center py-8">
      <v-alert type="warning" variant="tonal" class="mb-4">
        Contact niet gevonden
      </v-alert>
      <v-btn
        color="primary"
        variant="elevated"
        to="/contacts"
        prepend-icon="mdi-arrow-left"
      >
        Terug naar contacten
      </v-btn>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from "vue";
import { useRoute } from "vue-router";
import { useContactsStoreRefactor } from "@/stores/contactsStoreRefactor";
import { useMenuStore } from "@/stores/menuStore";
import { useFormsStore } from "@/stores/formsStore";
import ContactListItem from "@/components/contacts/ContactListItem.vue";

// Store instances and route
const route = useRoute();
const contactsStore = useContactsStoreRefactor();
const menuStore = useMenuStore();
const formsStore = useFormsStore();

// Reactive data
const isLoading = ref(false);
const error = ref(null);
const debugMode = ref(true) && import.meta.env.DEV; // Disable debug mode for production

// Simple contact computed property with better error handling
const contact = computed(() => {
  try {
    const contactId = Number(route.params.id);
    if (!contactId || isNaN(contactId)) {
      console.warn("Invalid contact ID:", route.params.id);
      return null;
    }

    const foundContact = contactsStore.getContactById(contactId);
    console.log("Contact lookup result:", { contactId, foundContact });
    return foundContact || null;
  } catch (err) {
    console.error("Error in contact computed:", err);
    return null;
  }
});

// Build full breadcrumb hierarchy
const breadcrumbItems = computed(() => {
  if (!contact.value) return [];

  const items = [
    {
      title: "Contacten",
      to: "/contacts",
      disabled: false,
      contact: null,
    },
  ];

  try {
    // Build hierarchy from current contact up to root
    const hierarchy = [];
    let currentContact = contact.value;

    // Add current contact
    hierarchy.unshift(currentContact);

    // Walk up the parent chain
    while (currentContact?.parent_id) {
      const parent = contactsStore.getContactById(currentContact.parent_id);
      if (parent) {
        hierarchy.unshift(parent);
        currentContact = parent;
      } else {
        break; // Stop if parent not found
      }
    }

    // Convert hierarchy to breadcrumb items
    hierarchy.forEach((contact, index) => {
      const isLast = index === hierarchy.length - 1;

      items.push({
        title: contact.display_name || contact.name || "Naamloos contact",
        to: `/contacts/${contact.id}`,
        disabled: isLast, // Disable current page
        contact: contact,
      });
    });
  } catch (err) {
    console.error("Error building breadcrumb:", err);
  }

  return items;
});

// Safe computed properties that won't cause infinite loops
const safeChildDepartments = computed(() => {
  if (!contact.value || contact.value.type !== "organization") return [];
  try {
    return (
      contactsStore.entities?.filter(
        (c) => c && c.parent_id === contact.value.id && c.type === "department",
      ) || []
    );
  } catch (err) {
    console.error("Error getting child departments:", err);
    return [];
  }
});

const safeChildPeople = computed(() => {
  if (
    !contact.value ||
    !["organization", "department"].includes(contact.value.type)
  )
    return [];
  try {
    return (
      contactsStore.entities?.filter(
        (c) => c && c.parent_id === contact.value.id && c.type === "individual",
      ) || []
    );
  } catch (err) {
    console.error("Error getting child people:", err);
    return [];
  }
});

// Contact categories
const contactCategories = computed(() => {
  if (!contact.value) return [];
  return contact.value.contact_categories || [];
});

// Contact detail accessors that handle different API response structures
const contactEmails = computed(() => {
  if (!contact.value) return [];

  // Try nested structure first (contact.contact_details.emails)
  if (contact.value.contact_details?.emails?.length) {
    return contact.value.contact_details.emails;
  }

  // Try flat structure (contact.emails)
  if (contact.value.emails?.length) {
    return contact.value.emails;
  }

  return [];
});

const contactPhones = computed(() => {
  if (!contact.value) return [];

  // Try nested structure first (contact.contact_details.phone_numbers)
  if (contact.value.contact_details?.phone_numbers?.length) {
    return contact.value.contact_details.phone_numbers;
  }

  // Try flat structure (contact.phone_numbers)
  if (contact.value.phone_numbers?.length) {
    return contact.value.phone_numbers;
  }

  return [];
});

const contactLocations = computed(() => {
  if (!contact.value) return [];

  // Get linked_locations (new approach)
  if (contact.value.linked_locations?.length) {
    return contact.value.linked_locations;
  }

  return [];
});

const contactAddresses = computed(() => {
  if (!contact.value) return [];

  // Try nested structure first (contact.contact_details.addresses)
  if (contact.value.contact_details?.addresses?.length) {
    return contact.value.contact_details.addresses;
  }

  // Try flat structure (contact.addresses)
  if (contact.value.addresses?.length) {
    return contact.value.addresses;
  }

  return [];
});

const contactNotes = computed(() => {
  if (!contact.value) return [];

  // Try nested structure first (contact.contact_details.notes)
  if (contact.value.contact_details?.notes?.length) {
    return contact.value.contact_details.notes;
  }

  // Try flat structure (contact.notes)
  if (contact.value.notes?.length) {
    return contact.value.notes;
  }

  return [];
});

// Check if contact has any contact details from either structure
const hasAnyContactDetails = computed(() => {
  return (
    contactEmails.value.length > 0 ||
    contactPhones.value.length > 0 ||
    contactLocations.value.length > 0 ||
    contactAddresses.value.length > 0 ||
    contactNotes.value.length > 0
  );
});

// Primary contact information to show next to name
const primaryContactInfo = computed(() => {
  const info = [];

  // Add primary email
  const primaryEmail =
    contactEmails.value.find((email) => email.primary) ||
    contactEmails.value[0];
  if (primaryEmail) {
    info.push(primaryEmail.email);
  } else if (contact.value?.primary_email_address) {
    info.push(contact.value.primary_email_address);
  }

  // Add primary phone
  const primaryPhone =
    contactPhones.value.find((phone) => phone.primary) ||
    contactPhones.value[0];
  if (primaryPhone) {
    info.push(primaryPhone.phone_number);
  } else if (contact.value?.primary_phone_formatted) {
    info.push(contact.value.primary_phone_formatted);
  }

  return info.join(" • ");
});

// Helper functions with fallbacks
const getContactIcon = (type) => {
  try {
    return contactsStore.icons?.[type]?.icon || "mdi-account";
  } catch (err) {
    return "mdi-account";
  }
};

const getContactColor = (type) => {
  try {
    return contactsStore.icons?.[type]?.color || "grey";
  } catch (err) {
    return "grey";
  }
};

const getContactTypeLabel = (type) => {
  const labels = {
    organization: "Bedrijf",
    department: "Afdeling",
    individual: "Persoon",
  };
  return labels[type] || "Onbekend";
};

const formatDate = (dateString) => {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("nl-NL", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch (err) {
    return dateString;
  }
};

// Methods
const loadContact = async () => {
  try {
    isLoading.value = true;
    error.value = null;

    const contactId = Number(route.params.id);
    console.log("Loading contact with ID:", contactId);

    if (!contactId || isNaN(contactId)) {
      throw new Error("Ongeldig contact ID");
    }

    // Ensure contacts are loaded
    if (!contactsStore.entities || contactsStore.entities.length === 0) {
      console.log("Loading all contacts first...");
      await contactsStore.getAll();
    }

    // Try to fetch the specific contact
    console.log("Fetching specific contact...");
    await contactsStore.fetchById(contactId);

    // Give Vue time to update
    await nextTick();

    // Check if contact was found after loading
    const foundContact = contactsStore.getContactById(contactId);
    if (!foundContact) {
      throw new Error("Contact niet gevonden");
    }

    console.log("Contact loaded successfully:", foundContact);
  } catch (err) {
    console.error("Error loading contact:", err);
    error.value =
      err.message || "Er is een fout opgetreden bij het laden van het contact";
  } finally {
    isLoading.value = false;
  }
};

const editContact = () => {
  try {
    if (!contact.value) return;

    formsStore.edit({
      type: "contact",
      item: { ...contact.value },
    });
  } catch (err) {
    console.error("Error opening edit form:", err);
  }
};

const addChildDepartment = () => {
  try {
    if (!contact.value) return;

    formsStore.edit({
      type: "contact",
      item: {
        parent_id: contact.value.id,
        type: "department",
      },
    });
  } catch (err) {
    console.error("Error opening create form:", err);
  }
};

const addChildPerson = () => {
  try {
    if (!contact.value) return;

    formsStore.edit({
      type: "contact",
      item: {
        parent_id: contact.value.id,
        type: "individual",
      },
    });
  } catch (err) {
    console.error("Error opening create form:", err);
  }
};

// Watch for route changes
watch(
  () => route.params.id,
  async (newId, oldId) => {
    if (newId !== oldId) {
      console.log("Route changed, reloading contact:", { newId, oldId });
      await loadContact();
    }
  },
);

// Lifecycle
onMounted(async () => {
  try {
    console.log("ContactDetail mounted, route params:", route.params);
    menuStore.setMenuVisible(true);
    await loadContact();
  } catch (err) {
    console.error("Error during ContactDetail mount:", err);
    error.value = "Er is een fout opgetreden bij het laden van de pagina";
  }
});

onUnmounted(() => {
  try {
    console.log("ContactDetail unmounted");
    isLoading.value = false;
    error.value = null;
  } catch (err) {
    console.error("Error during ContactDetail unmount:", err);
  }
});
</script>

<style scoped>
.contact-detail-page {
  max-width: 100%;
  padding: 0 16px;
}

.debug-info {
  font-family: monospace;
  border: 1px solid #ccc;
  border-radius: 4px;
}

.contact-content {
  opacity: 1;
  transition: opacity 0.3s ease;
}

.breadcrumb-nav {
  background: rgb(var(--v-theme-surface));
  border-radius: 8px;
  padding: 12px 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.breadcrumb-item {
  font-weight: 500;
}

.breadcrumb-item:hover:not(.v-breadcrumbs-item--disabled) {
  background-color: rgba(var(--v-theme-primary), 0.08);
  border-radius: 4px;
}

.contact-header {
  padding: 24px 0;
}

.contact-info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
}

.info-card {
  background: rgb(var(--v-theme-surface));
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.info-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  transform: translateY(-2px);
}

.info-card-header {
  display: flex;
  align-items: center;
  padding: 20px 24px 16px;
  border-bottom: 1px solid rgb(var(--v-theme-surface-variant));
}

.info-card-content {
  padding: 20px 24px 24px;
}

.contact-item {
  display: flex;
  align-items: flex-start;
  padding: 12px 0;
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.08);
}

.contact-item:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.contact-item-icon {
  margin-right: 16px;
  margin-top: 2px;
}

.contact-item-content {
  flex: 1;
}

.contact-item-label {
  font-size: 0.875rem;
  color: rgb(var(--v-theme-on-surface-variant));
  margin-bottom: 4px;
}

.contact-item-value {
  font-size: 1rem;
  color: rgb(var(--v-theme-on-surface));
  font-weight: 500;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 16px;
  text-align: center;
}

.contact-detail-group {
  margin-bottom: 16px;
}

.contact-detail-group:last-child {
  margin-bottom: 0;
}

.detail-group-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: rgb(var(--v-theme-primary));
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.notes-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.note-item {
  padding: 16px;
  background: rgba(var(--v-theme-primary), 0.04);
  border-radius: 8px;
  border-left: 4px solid rgb(var(--v-theme-primary));
  transition: all 0.2s ease;
}

.note-item:hover {
  background: rgba(var(--v-theme-primary), 0.08);
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(var(--v-theme-primary), 0.1);
}

.note-header {
  margin-bottom: 8px;
}

.note-content {
  line-height: 1.5;
  white-space: pre-wrap;
}

.children-section {
  margin-top: 32px;
}

.children-group {
  margin-bottom: 32px;
}

.children-header {
  margin-bottom: 16px;
}

.children-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.child-contact-item {
  background: rgb(var(--v-theme-surface));
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.child-contact-item:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transform: translateY(-1px);
}

.empty-children-inline {
  background: rgba(var(--v-theme-surface-variant), 0.3);
  border-radius: 8px;
  border: 2px dashed rgba(var(--v-theme-on-surface), 0.2);
}

.empty-children {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 32px;
  text-align: center;
  background: rgb(var(--v-theme-surface));
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .contact-detail-page {
    padding: 0 8px;
  }

  .contact-info-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .children-list {
    gap: 8px;
  }

  .info-card-header,
  .info-card-content {
    padding-left: 16px;
    padding-right: 16px;
  }

  .breadcrumb-nav {
    padding: 8px 12px;
  }

  .children-header .d-flex {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .children-header .d-flex > div:first-child {
    justify-content: center;
  }
}
</style>
